import * as React from 'react'
import { Link } from 'react-router-dom'

import { useMutationConfirmWebfleetBorders } from '@/lib/api/company/webfleet/useMutationConfirmWebfleetBorders'
import { useMutationPreviewWebfleetBorders } from '@/lib/api/company/webfleet/useMutationPreviewWebfleetBorders'
import {
  WebfleetBorderGroup,
  WebfleetBorderPreviewResponse,
} from '@/lib/api/company/webfleet/types'
import { toast } from '@/lib/toasts/api'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { getApiErrorMessage } from '@/modules/shared/utils/apiError'
import { SETTINGS_ROUTES } from '@/modules/settings/routes-typed'
import { Button } from '@/ui'
import { DateField } from '@/ui/form/controls/DateField'
import { Checkbox } from '@/ui/form/primitives'
import { Popup } from '@/ui/overlays/Popup'

type Props = {
  isOpen?: boolean
  handleClose?: () => void
  companyId: string
  onSuccess?: () => void
}

type Step = 'form' | 'preview'

const MS_PER_DAY = 24 * 60 * 60 * 1000
const MAX_RANGE_DAYS = 365

function daysBetween(start: string, end: string): number {
  const startMs = Date.parse(start)
  const endMs = Date.parse(end)
  if (Number.isNaN(startMs) || Number.isNaN(endMs)) return NaN
  return (endMs - startMs) / MS_PER_DAY
}

function initialSelectedKeys(groups: WebfleetBorderGroup[]): Set<string> {
  const keys = new Set<string>()
  for (const group of groups) {
    for (const item of group.items ?? []) {
      if (item.selected && !item.isDuplicate) {
        keys.add(item.itemKey)
      }
    }
  }
  return keys
}

function getApiErrorCode(err: unknown): string | undefined {
  const data = (err as { response?: { data?: unknown } })?.response?.data
  if (Array.isArray(data) && data.length > 0) {
    const first = data[0] as { code?: string; Code?: string }
    return first?.code ?? first?.Code
  }
  if (data && typeof data === 'object') {
    const record = data as { code?: string; Code?: string }
    return record.code ?? record.Code
  }
  return undefined
}

function formatDateTime(value?: string | null): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleString()
}

export const WebfleetBordersImportPopupContainer = ({
  isOpen,
  handleClose,
  companyId,
  onSuccess,
}: Props) => {
  const { t } = useLocalize()
  const previewMutation = useMutationPreviewWebfleetBorders(companyId)
  const confirmMutation = useMutationConfirmWebfleetBorders(companyId)

  const [step, setStep] = React.useState<Step>('form')
  const [startDate, setStartDate] = React.useState<string | null>(null)
  const [endDate, setEndDate] = React.useState<string | null>(null)
  const [isMinimalWages, setIsMinimalWages] = React.useState(false)
  const [filterOutEdgeDates, setFilterOutEdgeDates] = React.useState(false)
  const [dateError, setDateError] = React.useState<string | null>(null)
  const [preview, setPreview] =
    React.useState<WebfleetBorderPreviewResponse | null>(null)
  const [selectedKeys, setSelectedKeys] = React.useState<Set<string>>(
    () => new Set(),
  )

  const reset = React.useCallback(() => {
    setStep('form')
    setStartDate(null)
    setEndDate(null)
    setIsMinimalWages(false)
    setFilterOutEdgeDates(false)
    setDateError(null)
    setPreview(null)
    setSelectedKeys(new Set())
  }, [])

  React.useEffect(() => {
    if (!isOpen) {
      reset()
    }
  }, [isOpen, reset])

  const handleCloseOrCancel = () => {
    reset()
    handleClose?.()
  }

  const validateDates = (): boolean => {
    if (!startDate || !endDate) {
      setDateError(t('importModule.webfleet-date-required'))
      return false
    }
    const diff = daysBetween(startDate, endDate)
    if (Number.isNaN(diff) || diff < 0) {
      setDateError(t('importModule.webfleet-date-order'))
      return false
    }
    if (diff > MAX_RANGE_DAYS) {
      setDateError(t('importModule.webfleet-date-max-range'))
      return false
    }
    setDateError(null)
    return true
  }

  const handlePreview = async () => {
    if (!validateDates() || !startDate || !endDate) return

    try {
      const response = await previewMutation.mutateAsync({
        startDate,
        endDate,
        isMinimalWages,
        filterOutEdgeDates,
      })
      const data = response.data
      setPreview(data)
      setSelectedKeys(initialSelectedKeys(data.groups ?? []))
      setStep('preview')
    } catch (err) {
      const code = getApiErrorCode(err)
      if (code === 'MissingCredentials') {
        toast.error({
          text: t('importModule.webfleet-missing-credentials'),
          autoCloseMs: 8000,
        })
        return
      }
      toast.error({
        text:
          getApiErrorMessage(err) ||
          t('importModule.webfleet-preview-failed'),
      })
    }
  }

  const handleConfirm = async () => {
    if (!preview?.previewToken) return

    try {
      const response = await confirmMutation.mutateAsync({
        previewToken: preview.previewToken,
        isMinimalWages,
        filterOutEdgeDates,
        selectedItemKeys: Array.from(selectedKeys),
      })
      const savedCount = response.data?.savedCount ?? 0
      toast.success({
        text: t('importModule.webfleet-confirm-success', {
          count: savedCount,
        }),
      })
      handleCloseOrCancel()
      onSuccess?.()
    } catch (err) {
      const code = getApiErrorCode(err)
      if (code === 'MissingCredentials') {
        toast.error({
          text: t('importModule.webfleet-missing-credentials'),
          autoCloseMs: 8000,
        })
        return
      }
      toast.error({
        text:
          getApiErrorMessage(err) ||
          t('importModule.webfleet-confirm-failed'),
      })
    }
  }

  const toggleItem = (itemKey: string, checked: boolean) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev)
      if (checked) next.add(itemKey)
      else next.delete(itemKey)
      return next
    })
  }

  const toggleGroup = (group: WebfleetBorderGroup, checked: boolean) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev)
      for (const item of group.items ?? []) {
        if (item.isDuplicate) continue
        if (checked) next.add(item.itemKey)
        else next.delete(item.itemKey)
      }
      return next
    })
  }

  const integrationPath = SETTINGS_ROUTES.integration.buildPath({
    companyId,
  })

  const isBusy = previewMutation.isPending || confirmMutation.isPending

  return (
    <Popup
      open={isOpen}
      onClose={isBusy ? undefined : handleCloseOrCancel}
      closeOnBackdrop={!isBusy}
      closeOnEscape={!isBusy}
      showCloseButton={!isBusy}
      contentClassName="tw-w-[95vw] tw-max-w-[900px]"
    >
      <div className="tw-flex tw-flex-col tw-gap-4 tw-p-2">
        <div>
          <h2 className="tw-mb-1 tw-text-lg tw-font-semibold">
            {t('importModule.card-webfleet-borders-title')}
          </h2>
          <p className="tw-mb-0 tw-text-sm tw-text-content-base/70">
            {t('importModule.card-webfleet-borders-desc')}
          </p>
        </div>

        {step === 'form' && (
          <>
            <div className="tw-grid tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-2">
              <DateField
                label={t('importModule.webfleet-start-date')}
                value={startDate ?? ''}
                setValue={setStartDate}
                clearable={false}
                required
              />
              <DateField
                label={t('importModule.webfleet-end-date')}
                value={endDate ?? ''}
                setValue={setEndDate}
                clearable={false}
                required
              />
            </div>
            {dateError && (
              <p className="tw-mb-0 tw-text-sm tw-text-red-600">{dateError}</p>
            )}

            <label className="tw-flex tw-items-center tw-gap-2 tw-text-sm">
              <Checkbox
                checked={isMinimalWages}
                onChange={(e) => setIsMinimalWages(e.target.checked)}
              />
              {t('importModule.webfleet-is-minimal-wages')}
            </label>
            <label className="tw-flex tw-items-center tw-gap-2 tw-text-sm">
              <Checkbox
                checked={filterOutEdgeDates}
                onChange={(e) => setFilterOutEdgeDates(e.target.checked)}
              />
              {t('importModule.webfleet-filter-edge-dates')}
            </label>

            <p className="tw-mb-0 tw-text-xs tw-text-content-base/60">
              {t('importModule.webfleet-credentials-hint')}{' '}
              <Link
                to={integrationPath}
                className="tw-text-primary tw-underline"
              >
                {t('importModule.webfleet-credentials-link')}
              </Link>
            </p>

            <div className="tw-flex tw-justify-end tw-gap-2 tw-pt-2">
              <Button
                type="button"
                variant="tertiary"
                mode="outline"
                size="sm"
                onClick={handleCloseOrCancel}
                disabled={isBusy}
                text={t('importModule.webfleet-cancel')}
              />
              <Button
                type="button"
                variant="primary"
                size="sm"
                loading={previewMutation.isPending}
                disabled={isBusy}
                onClick={handlePreview}
                text={t('importModule.webfleet-preview')}
              />
            </div>
          </>
        )}

        {step === 'preview' && preview && (
          <>
            <div className="tw-grid tw-grid-cols-3 tw-gap-2 tw-rounded-md tw-bg-base-200 tw-p-3 tw-text-sm">
              <div>
                <div className="tw-text-content-base/60">
                  {t('importModule.webfleet-summary-valid')}
                </div>
                <div className="tw-font-semibold">{preview.valid}</div>
              </div>
              <div>
                <div className="tw-text-content-base/60">
                  {t('importModule.webfleet-summary-invalid')}
                </div>
                <div className="tw-font-semibold">{preview.invalid}</div>
              </div>
              <div>
                <div className="tw-text-content-base/60">
                  {t('importModule.webfleet-summary-duplicates')}
                </div>
                <div className="tw-font-semibold">{preview.duplicateCount}</div>
              </div>
            </div>

            {preview.missingVehicles?.length > 0 && (
              <div className="tw-rounded-md tw-border tw-border-amber-200 tw-bg-amber-50 tw-p-3 tw-text-sm tw-text-amber-900">
                <div className="tw-mb-1 tw-font-medium">
                  {t('importModule.webfleet-missing-vehicles')}
                </div>
                <div>{preview.missingVehicles.join(', ')}</div>
              </div>
            )}

            {preview.logs?.length > 0 && (
              <div className="tw-max-h-28 tw-overflow-y-auto tw-rounded-md tw-border tw-border-base-300 tw-bg-base-100 tw-p-2 tw-text-xs tw-text-content-base/80">
                {preview.logs.map((log, i) => (
                  <div key={`${i}-${log}`}>{log}</div>
                ))}
              </div>
            )}

            <div className="tw-max-h-[45vh] tw-overflow-y-auto tw-rounded-md tw-border tw-border-base-300">
              {(preview.groups ?? []).map((group) => {
                const selectableItems = (group.items ?? []).filter(
                  (item) => !item.isDuplicate,
                )
                const selectedInGroup = selectableItems.filter((item) =>
                  selectedKeys.has(item.itemKey),
                ).length
                const allSelected =
                  selectableItems.length > 0 &&
                  selectedInGroup === selectableItems.length
                const someSelected =
                  selectedInGroup > 0 && selectedInGroup < selectableItems.length

                return (
                  <div
                    key={group.groupKey}
                    className="tw-border-b tw-border-base-300 last:tw-border-b-0"
                  >
                    <div className="tw-flex tw-items-center tw-gap-2 tw-bg-base-200 tw-px-3 tw-py-2 tw-text-sm tw-font-medium">
                      <Checkbox
                        checked={allSelected}
                        indeterminate={someSelected}
                        disabled={selectableItems.length === 0}
                        onChange={(e) =>
                          toggleGroup(group, e.target.checked)
                        }
                      />
                      <span>
                        {group.driverFullName ||
                          t('importModule.webfleet-unknown-driver')}
                        {' · '}
                        {group.vehicleRegistrationNumber ||
                          t('importModule.webfleet-unknown-vehicle')}
                      </span>
                      <span className="tw-ml-auto tw-text-xs tw-font-normal tw-text-content-base/60">
                        {t('importModule.webfleet-group-counts', {
                          valid: group.valid,
                          invalid: group.invalid,
                        })}
                      </span>
                    </div>
                    <ul className="tw-mb-0 tw-list-none tw-p-0">
                      {(group.items ?? []).map((item) => (
                        <li
                          key={item.itemKey}
                          className="tw-flex tw-items-center tw-gap-2 tw-border-t tw-border-base-200 tw-px-3 tw-py-1.5 tw-text-sm"
                        >
                          <Checkbox
                            checked={selectedKeys.has(item.itemKey)}
                            disabled={item.isDuplicate}
                            onChange={(e) =>
                              toggleItem(item.itemKey, e.target.checked)
                            }
                          />
                          <span className="tw-min-w-[3rem] tw-font-medium">
                            {item.country || '—'}
                          </span>
                          <span className="tw-text-content-base/70">
                            {formatDateTime(item.arriveDate)}
                            {' → '}
                            {formatDateTime(item.leaveDate)}
                          </span>
                          {item.isDuplicate && (
                            <span className="tw-ml-auto tw-rounded tw-bg-base-300 tw-px-1.5 tw-py-0.5 tw-text-xs">
                              {t('importModule.webfleet-duplicate')}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )
              })}
              {(preview.groups ?? []).length === 0 && (
                <div className="tw-p-4 tw-text-sm tw-text-content-base/60">
                  {t('importModule.webfleet-no-items')}
                </div>
              )}
            </div>

            <div className="tw-flex tw-justify-between tw-gap-2 tw-pt-2">
              <Button
                type="button"
                variant="tertiary"
                mode="outline"
                size="sm"
                onClick={() => setStep('form')}
                disabled={isBusy}
                text={t('importModule.webfleet-back')}
              />
              <div className="tw-flex tw-gap-2">
                <Button
                  type="button"
                  variant="tertiary"
                  mode="outline"
                  size="sm"
                  onClick={handleCloseOrCancel}
                  disabled={isBusy}
                  text={t('importModule.webfleet-cancel')}
                />
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  loading={confirmMutation.isPending}
                  disabled={isBusy || selectedKeys.size === 0}
                  onClick={handleConfirm}
                  text={t('importModule.webfleet-confirm')}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </Popup>
  )
}
